import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray, Field, useFormikContext } from "formik";
import {
  Form as BootstrapForm,
  Card,
  Button,
  Table,
  Row,
  Col,
  Badge,
  InputGroup,
  Spinner,
  FormControl,
} from "react-bootstrap";
import { BiSave, BiTrash, BiPlus, BiLeftArrowAlt } from "react-icons/bi";
import { FaHashtag, FaWrench, FaEdit } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { fetchSuppliers } from "../../redux/features/supplier.features";
import {
  createPurchase,
  updatePurchase,
  getAllPurchases,
  checkReferenceNumber,
} from "../../redux/features/purchase.features";
import { getAllItemsWithStock } from "../../redux/features/stock.features";
import { formatCurrency } from "../../utility";
import SupplierField from "../core/shared/fields/SupplierField";
import ItemField from "../core/shared/fields/ItemField";
import FormikDraftPersist from "./FormikDraftPersist";
import DynamicDatePicker from "../core/shared/components/DynamicDatePicker";
import VariantAndUnitsModal from "./VariantAndUnitsModal";
import SerializedItemRow from "./SerializedItemRow"; // 👈 Import the new component
import SupplierDetails from "../suppliers/SupplierDetails"; // Import
import CustomerSearchField from "../users/CustomerSearchField";
import { FaUserTag, FaTruckLoading, FaFingerprint, FaShieldAlt, FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";
import { fetchCustomers } from "../../redux/features/customer.features";
import CustomerForm from "../customers/CustomerForm";
import BrandModal from "../core/components/Brand/BrandModal";
import B2BPricingModal from "./B2BPricingModal"; // Import the B2B pricing modal

const validationSchema = Yup.object().shape({
  purchase_type: Yup.string().oneOf(["Supplier", "Trade-In"]).required(),
  supplier: Yup.string().when("purchase_type", {
    is: "Supplier",
    then: (schema) => schema.required("Supplier is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  customer: Yup.string().when("purchase_type", {
    is: "Trade-In",
    then: (schema) => schema.required("Customer is required for Trade-In"),
    otherwise: (schema) => schema.nullable(),
  }),
  purchaseDate: Yup.date().required("Purchase date is required"),
  referenceNumber: Yup.string().required("Reference number is required"),
  purchasedItems: Yup.array()
    .of(
      Yup.object().shape({
        item_id: Yup.string().required("Item is required."),
        purchaseQty: Yup.number()
          .min(1, "Qty must be > 0.")
          .required("Qty is required."),
        unitCost: Yup.number()
          .min(0, "Cost cannot be negative.")
          .required("Cost is required."),
        serializedItems: Yup.array().of(
          Yup.object().shape({
            serialNumber: Yup.string()
          })
        )
      })
    )
    .min(1, "At least one item is required.")
    .test('unique-serials', 'Duplicate serial numbers found in purchase order', function (value) {
      if (!value) return true;
      const allSerials = value.reduce((acc, item) => {
        if (item.serializedItems) {
          item.serializedItems.forEach(si => {
            if (si.serialNumber?.trim()) acc.push(si.serialNumber.trim());
          });
        }
        return acc;
      }, []);
      const uniqueSerials = new Set(allSerials);
      return uniqueSerials.size === allSerials.length;
    }),
});

const CreatePurchaseForm = ({ editMode, initialValues, onSubmit }) => {
  const dispatch = useDispatch();
  const { suppliers, searchResults } = useSelector((state) => state.supplierReducer); // Include searchResults
  const { stocks: availableItems } = useSelector((state) => state.stockReducer);

  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [showB2BModal, setShowB2BModal] = useState(false);
  const [b2bItemIndex, setB2BItemIndex] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [selectedBaseItem, setSelectedBaseItem] = useState(null);
  const [showSupplierDetails, setShowSupplierDetails] = useState(false);

  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchCustomers());
    dispatch(getAllItemsWithStock({ includeEmptyBase: true }));
  }, [dispatch]);

  // Helper to find supplier from both lists
  const getSelectedSupplier = (id) => {
    if (!id) return null;
    return [...(suppliers || []), ...(searchResults || [])].find(s => s._id === id);
  };

  const { customers } = useSelector((state) => state.customersReducer);
  const getSelectedCustomer = (id) => {
    if (!id) return null;
    return (customers || []).find(c => c._id === id);
  };

  const savePurchaseOrder = (values, { resetForm, setSubmitting }) => {
    // 4. Mandatory KYC check for Trade-In
    if (values.purchase_type === "Trade-In") {
      const selectedCustomer = getSelectedCustomer(values.customer);
      if (!selectedCustomer?.nic_number) {
        toast.error("Legal Compliance Error: NIC/ID is required for Trade-In transactions.");
        setShowKYCModal(true);
        setSubmitting(false);
        return;
      }

      // 5. Item limitation for Trade-In (Serialized Only)
      const hasNonSerialized = values.purchasedItems.some(item => {
        const itemInfo = (availableItems || []).find(i => i._id?.toString() === item.item_id?.toString());
        return !itemInfo?.serialized && !itemInfo?.isSerialized;
      });

      if (hasNonSerialized) {
        toast.error("Trade-In Policy: Only serialized devices (with IMEI/Serial) can be accepted for trade-in.");
        setSubmitting(false);
        return;
      }
    }

    // 1. Validation: Ensure all serialized items have valid serial numbers
    for (const item of values.purchasedItems) {
      if (!item.item_id) continue; // Skip empty rows

      const itemDetails = (availableItems || []).find(
        (i) =>
          i._id?.toString() === item.item_id?.toString() &&
          (item.variant_id
            ? i.variant_id?.toString() === item.variant_id?.toString()
            : !i.variant_id)
      );

      const isReallySerialized =
        itemDetails?.isSerialized ||
        itemDetails?.serialized ||
        item.isSerialized === true ||
        item.isSerialized === "true" ||
        (item.serializedItems && item.serializedItems.length > 0) ||
        itemDetails?.category === "Device";

      if (isReallySerialized) {
        if (
          !item.serializedItems ||
          Number(item.serializedItems.length) !== Number(item.purchaseQty)
        ) {
          toast.error(
            `Mismatch! Expected ${item.purchaseQty} serials for ${item.itemName || "Serialized Item"}, but found ${item.serializedItems?.length || 0}.`
          );
          setSubmitting(false); // Fix: enable button back
          return;
        }

        const hasEmptySerials = item.serializedItems.some(
          (s) => !s.serialNumber || !s.serialNumber.toString().trim()
        );
        if (hasEmptySerials) {
          toast.error(
            `Please enter all Serial Numbers for ${item.itemName || "Serialized Item"}`
          );
          setSubmitting(false); // Fix: enable button back
          return;
        }
      }
    }

    // This function submits the final, correct data to your backend
    const finalValues = {
      ...values,
      purchasedItems: values.purchasedItems.map((item) => {
        const itemDetails = availableItems.find((i) => i._id === item.item_id);
        // For simple serialized items, calculate total from sub-items if necessary
        if (itemDetails?.serialized && itemDetails?.category !== "Device") {
          // const total = item.serializedItems.reduce((acc, si) => acc + Number(si.unitCost || 0), 0);
          // return { ...item, total_price: total };
        }
        return { ...item, total_price: item.purchaseQty * item.unitCost };
      }),
    };

    console.log(
      "🚨 savePurchaseOrder triggered",
      new Date().toISOString(),
      values
    );
    console.log("Final Values to submit Purchase Id : ", initialValues?._id);

    const action = editMode
      ? updatePurchase({ id: initialValues._id, updatedData: values })
      : createPurchase(values);
    dispatch(action)
      .then((res) => {
        setSubmitting(false);
        if (res.error) {
          console.error(res.error);
          // Fix: Check payload for server message (from rejectWithValue)
          const serverMsg = res.payload?.message || res.payload?.error || res.error.message || 'Failed to save Purchase';
          toast.error(`Error: ${serverMsg}`);
          return;
        }
        toast.success(
          `Purchase ${editMode ? "updated" : "created"} successfully`
        );
        try { localStorage.removeItem("ishop_purchase_draft_create"); } catch (e) { }
        dispatch(getAllPurchases());
        resetForm();
        if (typeof onSubmit === "function") onSubmit(res.payload);
      })
      .catch((err) => {
        console.error("Error saving purchase:", err);
        setSubmitting(false);
        toast.error(`Failed to save Purchase`);
      });
  };

  const initialData = React.useMemo(() => {
    // Normalize purchasedItems to handle populated objects (from Backend) vs IDs (Expected by Form)
    const normalizedItems = (initialValues?.purchasedItems || []).map(item => {
      const itemId = item.item_id?._id || item.item_id;
      const variantId = item.variant_id?._id || item.variant_id;

      return {
        ...item,
        item_id: itemId, // Ensure String ID
        variant_id: variantId, // Ensure String ID
        // Persist Name/Category in case Redux lookup fails or hasn't loaded yet
        itemName: item.item_id?.itemName || item.itemName,
        category: item.item_id?.category || item.category
      };
    });

    return {
      purchase_type: "Supplier",
      supplier: "",
      customer: "",
      purchaseDate: new Date(),
      referenceNumber: "",
      purchase_tax: 0,
      purchase_discount: 0,
      purchase_status: "Pending Verification",
      ...initialValues,
      purchasedItems: normalizedItems, // Use normalized items
    };
  }, [initialValues]);

  return (
    <Formik
      initialValues={initialData}
      validationSchema={validationSchema}
      onSubmit={savePurchaseOrder}
    >
      {({
        values,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        resetForm,
        errors,
        touched,
        validateForm,
        setFieldError,
        setFieldTouched,
      }) => {
        const handleReferenceBlur = async (e) => {
          const value = e.target.value;
          setFieldTouched("referenceNumber", true);
          if (!value) return;


          try {
            const res = await dispatch(checkReferenceNumber(value)).unwrap();
            if (res.exists) {
              setFieldError("referenceNumber", "Reference Number already exists in the system.");
              toast.warning(`Reference Number "${value}" is already used.`);
            }
          } catch (err) {
            console.error("Error checking reference:", err);
          }
        };

        const handleFormSubmit = async (e) => {
          e.preventDefault();
          const formErrors = await validateForm();
          if (Object.keys(formErrors).length > 0) {
            console.log("Validation Errors:", formErrors);
            toast.error("Please fix validation errors before submitting.");
            // Optional: scroll to first error
          }
          handleSubmit(e);
        };

        const handleItemSelection = (newItem, index) => {
          if (!newItem || !newItem._id) return;

          const itemId = newItem._id.toString();
          const cost = newItem.lastUnitCost || 0;
          const sellingPrice = newItem.lastSellingPrice || 0;
          const isSer = newItem.isSerialized || newItem.serialized;

          const baseData = {
            item_id: itemId,
            variant_id: newItem.variant_id || null,
            itemName: newItem.itemName,
            isSerialized: isSer,
            purchaseQty: 1,
            unitCost: cost,
            sellingPrice: sellingPrice,
            total_price: cost,
            variantAttributes: newItem?.variantAttributes || [],
            serializedItems: isSer ? [{
              serialNumber: "",
              unitCost: cost,
              sellingPrice: sellingPrice,
              condition: "Brand New"
            }] : [],
            condition: "Brand New"
          };

          setFieldValue(`purchasedItems[${index}]`, baseData);

          if (newItem.category === "Device") {
            setCurrentItemIndex(index);
            setSelectedBaseItem(newItem);
            setShowVariantModal(true);
          }
        };

        const handleModalSave = (dataFromModal) => {
          if (currentItemIndex !== null && values.purchasedItems[currentItemIndex]) {
            const existingRow = values.purchasedItems[currentItemIndex];
            // console.log("currentItemIndex", currentItemIndex);
            // console.log("existingRow", existingRow);
            // console.log("✅ Saving Modal Data to Table Row:", dataFromModal);

            const itemData = {
              ...existingRow,
              ...dataFromModal,
              item_id: (dataFromModal.item_id || existingRow.item_id)?.toString(),
              variant_id: dataFromModal.variant_id || existingRow.variant_id || null
            };

            console.log("Final itemData to set in form:", itemData);
            setFieldValue(`purchasedItems[${currentItemIndex}]`, itemData);
          }
          setShowVariantModal(false);
          setCurrentItemIndex(null);
          setSelectedBaseItem(null);
        };

        return (
          <Form
            onSubmit={handleFormSubmit}
            className="purchase-form-widget fade-in-up"
          >
            <FormikDraftPersist storageKey="ishop_purchase_draft_create" isEdit={editMode} />
            <Card className="border-0 shadow-sm rounded-3">
              <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span className="p-2 bg-primary bg-opacity-10 text-primary rounded-circle">
                    <FaWrench size={18} />
                  </span>
                  <h5 className="mb-0 fw-bold text-dark">{editMode ? "Edit Purchase Order" : "Create New Purchase"}</h5>
                </div>
                <div className="text-muted small">
                  {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {/* TOP SECTION: TYPE, SUPPLIER & META */}
                <Row className="mb-4 g-4">
                  <Col md={12}>
                    <div className="bg-light p-2 rounded-3 d-inline-flex gap-2">
                      <Button
                        variant={values.purchase_type === "Supplier" ? "primary" : "light"}
                        size="sm"
                        className="rounded-pill px-4 fw-bold d-flex align-items-center gap-2"
                        onClick={() => {
                          setFieldValue("purchase_type", "Supplier");
                          setFieldValue("customer", "");
                        }}
                      >
                        <FaTruckLoading /> Regular Purchase
                      </Button>
                      <Button
                        variant={values.purchase_type === "Trade-In" ? "info" : "light"}
                        size="sm"
                        className="rounded-pill px-4 fw-bold d-flex align-items-center gap-2"
                        onClick={() => {
                          setFieldValue("purchase_type", "Trade-In");
                          setFieldValue("supplier", "");
                        }}
                      >
                        <FaUserTag /> Trade-In (Customer Purchase)
                      </Button>
                    </div>
                  </Col>

                  <Col md={7}>
                    <div className="d-flex flex-column h-100">
                      <label className="small text-muted fw-bold text-uppercase mb-2">
                        {values.purchase_type === "Trade-In" ? "Customer Identity" : "Supplier Details"}
                      </label>

                      {values.purchase_type === "Trade-In" ? (
                        /* CUSTOMER / TRADE-IN LOGIC */
                        values.customer ? (
                          (() => {
                            const selectedCustomer = getSelectedCustomer(values.customer);
                            return (
                              <Card className="border border-info bg-light shadow-sm h-100 animate__animated animate__fadeIn">
                                <Card.Body className="p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="rounded-circle bg-info bg-opacity-10 text-info d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                        <span className="fw-bold fs-5">{selectedCustomer?.first_name?.charAt(0) || 'C'}</span>
                                      </div>
                                      <div>
                                        <h6 className="fw-bold mb-0 text-dark">{selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : 'Unknown Customer'}</h6>
                                        <div className="text-muted small d-flex align-items-center gap-2">
                                          <span>{selectedCustomer?.phone_number}</span>
                                          {selectedCustomer?.nic_number && <span className="badge bg-white text-info border border-info">NIC: {selectedCustomer.nic_number}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      className="px-3 rounded-pill bg-white"
                                      onClick={() => setFieldValue("customer", "")}
                                    >
                                      Change
                                    </Button>
                                  </div>
                                  <div className="row g-2 border-top pt-3">
                                    <div className="col-12">
                                      <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex flex-column">
                                          <span className="x-small text-uppercase fw-bold text-muted mb-1">KYC Status</span>
                                          {selectedCustomer?.nic_number ? (
                                            <span className="badge bg-success text-white rounded-pill align-self-start py-2 px-3 shadow-sm">
                                              <FaShieldAlt className="me-2" /> Verified Identity
                                            </span>
                                          ) : (
                                            <span className="badge bg-danger text-white rounded-pill align-self-start py-2 px-3 shadow-sm">
                                              <FaFingerprint className="me-2" /> KYC Incomplete
                                            </span>
                                          )}
                                        </div>

                                        {!selectedCustomer?.nic_number && (
                                          <Button
                                            variant="danger"
                                            size="sm"
                                            className="fw-bold rounded-pill px-3 shadow-sm"
                                            onClick={() => setShowKYCModal(true)}
                                          >
                                            <FaEdit className="me-2" /> Complete KYC
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-12 mt-3">
                                      <div className="alert alert-danger d-flex align-items-start gap-3 border-0 shadow-sm rounded-3 mb-0">
                                        <FaExclamationTriangle className="mt-1 flex-shrink-0" size={20} />
                                        <div className="small">
                                          <strong className="d-block mb-1">Legal Compliance Warning (CID/Police)</strong>
                                          Purchasing used devices without verified National Identity (NIC) or Passport is a criminal offense. You are liable for "Stolen Property" charges if the device is blacklisted. **Physical ID verification is mandatory.**
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            );
                          })()
                        ) : (
                          <div className="bg-light p-4 rounded-3 border border-info border-dashed text-center d-flex flex-column justify-content-center h-100 shadow-sm" style={{ borderStyle: 'dashed' }}>
                            <div className="mb-3">
                              <span className="badge bg-info bg-opacity-10 text-info px-3 py-2 rounded-pill small fw-bold">
                                <FaShieldAlt className="me-2" /> CID Compliance Mode Active
                              </span>
                            </div>
                            <CustomerSearchField
                              label={"Search/Select Customer"}
                              name={"customer"}
                              customerId={values.customer}
                              onChange={(e) => setFieldValue("customer", e.target.value)}
                            />
                            {errors.customer && touched.customer && (
                              <div className="text-danger small mt-2 text-start">
                                {errors.customer}
                              </div>
                            )}
                            <small className="text-muted mt-2">
                              For Trade-Ins, legal ID verification is mandatory.
                            </small>
                          </div>
                        )
                      ) : (
                        /* SUPPLIER LOGIC (Original) */
                        values.supplier ? (
                          (() => {
                            const selectedSupplier = getSelectedSupplier(values.supplier);
                            return (
                              <Card className="border bg-light shadow-sm h-100 animate__animated animate__fadeIn">
                                <Card.Body className="p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                        <span className="fw-bold fs-5">{selectedSupplier?.business_name?.charAt(0) || 'S'}</span>
                                      </div>
                                      <div>
                                        <h6 className="fw-bold mb-0 text-dark">{selectedSupplier?.business_name || 'Unknown Supplier'}</h6>
                                        <div className="text-muted small d-flex align-items-center gap-2">
                                          <span>{selectedSupplier?.contact_person}</span>
                                          {selectedSupplier?.phone && <span className="badge bg-white text-secondary border">{selectedSupplier.phone}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline-dark"
                                      size="sm"
                                      className="px-3 rounded-pill bg-white border-secondary"
                                      onClick={() => setFieldValue("supplier", "")}
                                    >
                                      Change
                                    </Button>
                                  </div>
                                  {errors.supplier && touched.supplier && (
                                    <div className="text-danger small mt-2">
                                      {errors.supplier}
                                    </div>
                                  )}

                                  <div className="row g-2 border-top pt-3 cursor-pointer" onClick={() => setShowSupplierDetails(true)} style={{ cursor: 'pointer' }} title="Click to view full history">
                                    <div className="col-6 border-end">
                                      <div className="d-flex flex-column align-items-center">
                                        <span className="x-small text-uppercase fw-bold text-muted mb-1">Total Purchases</span>
                                        <span className="fw-bold text-dark">{formatCurrency(selectedSupplier?.totalPurchaseVolume || 0)}</span>
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <div className="d-flex flex-column align-items-center">
                                        <span className="x-small text-uppercase fw-bold text-muted mb-1">Payment Due</span>
                                        <span className="fw-bold text-danger">
                                          {formatCurrency(selectedSupplier?.account?.balance || selectedSupplier?.financial?.current_balance || 0)}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="col-12 text-center mt-2">
                                      <span className="badge bg-light text-primary border rounded-pill small px-3">View History</span>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            );
                          })()
                        ) : (
                          <div className="bg-light p-4 rounded-3 border border-dashed text-center d-flex flex-column justify-content-center h-100">
                            <SupplierField
                              selectedSupplierId={values.supplier}
                              suppliers={suppliers}
                              onAddSupplier={(s) => setFieldValue("supplier", s._id)}
                              isInvalid={!!(errors.supplier && touched.supplier)}
                            />
                            {errors.supplier && touched.supplier && (
                              <div className="text-danger small mt-2 text-start">
                                {errors.supplier}
                              </div>
                            )}
                            <small className="text-muted mt-2">
                              Search or add a new supplier to begin
                            </small>
                          </div>
                        )
                      )}
                    </div>
                  </Col>

                  <Col md={5}>
                    <div className="d-flex flex-column h-100">
                      <label className="small text-muted fw-bold text-uppercase mb-2">Order Details</label>
                      <Card className="h-100 border bg-white shadow-sm">
                        <Card.Body className="p-3">
                          <div className="d-flex flex-column gap-3">
                            <div>
                              <label className="form-label small text-muted mb-1">Purchase Date</label>
                              <div className="w-100">
                                <DynamicDatePicker
                                  fieldName="purchaseDate"
                                  className={
                                    errors.purchaseDate && touched.purchaseDate
                                      ? "is-invalid"
                                      : ""
                                  }
                                />
                                {errors.purchaseDate && touched.purchaseDate && (
                                  <div className="text-danger x-small mt-1">
                                    {errors.purchaseDate}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="form-label small text-muted mb-1">Reference No</label>
                              <InputGroup>
                                <InputGroup.Text className="bg-light border-end-0 text-muted"><FaHashtag size={14} /></InputGroup.Text>
                                <Field
                                  as={FormControl}
                                  name="referenceNumber"
                                  onBlur={handleReferenceBlur}
                                  className={`border-start-0 ps-0 bg-light fw-medium ${errors.referenceNumber &&
                                    touched.referenceNumber
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  placeholder="E.g. PO-2023-001"
                                />
                              </InputGroup>
                              {errors.referenceNumber &&
                                touched.referenceNumber && (
                                  <div className="text-danger x-small mt-1">
                                    {errors.referenceNumber}
                                  </div>
                                )}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </Col>
                </Row>

                {/* MIDDLE SECTION: ITEMS TABLE */}
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                      <span className="badge bg-primary rounded-pill">{(values.purchasedItems || []).length}</span>
                      Inventory Items
                    </h6>
                  </div>
                  <div className="table-responsive rounded-3 border">
                    <FieldArray name="purchasedItems">
                      {({ remove, push }) => (
                        <Table hover className="align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                          <thead className="bg-light text-muted text-uppercase small">
                            <tr>
                              <th className="py-3 ps-3 border-0">#</th>
                              <th className="py-3 border-0" style={{ minWidth: "280px" }}>Item Details</th>
                              <th className="py-3 border-0" style={{ minWidth: "100px" }}>Qty</th>
                              <th className="py-3 border-0" style={{ width: "140px" }}>Condition</th>
                              <th className="py-3 border-0" style={{ width: "130px" }}>Unit Cost</th>
                              <th className="py-3 border-0" style={{ width: "130px" }}>Selling Price</th>
                              <th className="py-3 border-0 text-end pe-4">Total</th>
                              <th className="py-3 border-0 text-center" style={{ width: "80px" }}></th>
                            </tr>
                          </thead>
                          <tbody className="border-top-0">
                            {(values.purchasedItems || []).map((item, index) => {
                              const itemDetails = (availableItems || []).find(
                                (i) => i._id?.toString() === item.item_id?.toString() &&
                                  (item.variant_id ? i.variant_id?.toString() === item.variant_id?.toString() : !i.variant_id)
                              );
                              const isPlaceholder = !item.item_id;

                              // ROBUST CHECK: Fallback to local item properties if catalog lookup (itemDetails) fails
                              const isReallySerialized =
                                itemDetails?.isSerialized ||
                                itemDetails?.serialized ||
                                item.isSerialized === true ||
                                item.isSerialized === "true" ||
                                (item.serializedItems && item.serializedItems.length > 0);

                              {/* RENDER ROW COMPONENT */ }
                              if (isReallySerialized) {
                                return (
                                  <SerializedItemRow
                                    key={index}
                                    item={item}
                                    itemDetails={itemDetails || { itemName: item.itemName, isVariant: !!item.variant_id }}
                                    index={index}
                                    remove={remove}
                                    onOpenB2B={() => {
                                      setB2BItemIndex(index);
                                      setShowB2BModal(true);
                                    }}
                                    onEditVariant={() => {
                                      setCurrentItemIndex(index);
                                      setSelectedBaseItem(itemDetails || {
                                        _id: item.item_id,
                                        itemName: item.itemName,
                                        serialized: true,
                                        isSerialized: true,
                                        category: 'Device',
                                        variant_id: item.variant_id,
                                        variantAttributes: item.variantAttributes
                                      });
                                      setShowVariantModal(true);
                                    }}
                                    onQtyChange={(newQty) => {
                                      // If it's a Device, we MUST use the modal to manage units (due to battery/IMEI complexity)
                                      if (itemDetails?.category === "Device") {
                                        setCurrentItemIndex(index);
                                        setSelectedBaseItem(itemDetails || {
                                          _id: item.item_id,
                                          itemName: item.itemName,
                                          serialized: true,
                                          isSerialized: true,
                                          category: 'Device',
                                          variant_id: item.variant_id,
                                          variantAttributes: item.variantAttributes
                                        });
                                        setShowVariantModal(true);
                                        return;
                                      }

                                      const currentSerials = values.purchasedItems[index].serializedItems || [];
                                      let updatedSerials = [...currentSerials];

                                      if (newQty > currentSerials.length) {
                                        for (let i = currentSerials.length; i < newQty; i++) {
                                          updatedSerials.push({
                                            serialNumber: "",
                                            unitCost: item.unitCost || 0,
                                            sellingPrice: item.sellingPrice || 0,
                                            condition: item.condition || "Brand New"
                                          });
                                        }
                                      } else if (newQty < currentSerials.length) {
                                        updatedSerials = updatedSerials.slice(0, newQty);
                                      }

                                      setFieldValue(`purchasedItems[${index}].serializedItems`, updatedSerials);
                                      setFieldValue(`purchasedItems[${index}].purchaseQty`, newQty);
                                      setFieldValue(`purchasedItems[${index}].total_price`, newQty * (item.unitCost || 0));
                                    }}
                                  />
                                );
                              }

                              // DEFAULT ROW
                              return (
                                <tr key={index}>
                                  <td className="ps-3 text-muted">{index + 1}</td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      {item.item_id && (
                                        <div className="d-flex gap-1">
                                          {(itemDetails?.isVariant || item.variant_id) ? (
                                            <Badge bg="info" style={{ fontSize: '0.6rem' }}>Variant</Badge>
                                          ) : (
                                            <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>Base</Badge>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <ItemField
                                      defaultInputValue={itemDetails?.itemName || item.itemName}
                                      items={availableItems}
                                      onAddItem={(newItem) => handleItemSelection(newItem, index)}
                                    />
                                    {isPlaceholder && <div className="text-muted fst-italic small mt-1 ms-1">Search to add item...</div>}
                                  </td>

                                  <td style={{ minWidth: '100px' }}>
                                    <InputGroup size="sm">
                                      <FormControl
                                        type="number"
                                        value={item.purchaseQty}
                                        disabled={isPlaceholder}
                                        className="text-center fw-medium"
                                        min="1"
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value) || 0;
                                          setFieldValue(`purchasedItems[${index}].purchaseQty`, val);
                                          setFieldValue(`purchasedItems[${index}].total_price`, val * (item.unitCost || 0));
                                        }}
                                      />
                                    </InputGroup>
                                  </td>

                                  <td>
                                    <Field as={BootstrapForm.Select} size="sm" name={`purchasedItems[${index}].condition`} className="form-select-sm">
                                      <option value="Brand New">Brand New</option>
                                      <option value="Like New">Like New</option>
                                      <option value="Open Box">Open Box</option>
                                      <option value="Used Grade A">Used Grade A</option>
                                      <option value="Used Grade B">Used Grade B</option>
                                      <option value="Used">Used</option>
                                      <option value="Pre-Owned">Pre-Owned</option>
                                      <option value="Refurbished">Refurbished</option>
                                      <option value="Active">Active</option>
                                      <option value="Non-Active">Non-Active</option>
                                      <option value="Damaged">Damaged</option>
                                    </Field>
                                  </td>

                                  <td>
                                    <div className="input-group input-group-sm">
                                      <FormControl
                                        type="number"
                                        value={item.unitCost}
                                        disabled={isPlaceholder}
                                        className="text-end"
                                        onChange={(e) => {
                                          const val = parseFloat(e.target.value) || 0;
                                          setFieldValue(`purchasedItems[${index}].unitCost`, val);
                                          setFieldValue(`purchasedItems[${index}].total_price`, val * item.purchaseQty);

                                          // Propagate to serialized items
                                          if (item.isSerialized && item.serializedItems) {
                                            const updatedSerials = item.serializedItems.map(s => ({ ...s, unitCost: val }));
                                            setFieldValue(`purchasedItems[${index}].serializedItems`, updatedSerials);
                                          }
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="input-group input-group-sm mb-1">
                                      <FormControl
                                        type="number"
                                        value={item.sellingPrice}
                                        className="text-end"
                                        onChange={(e) => {
                                          const val = parseFloat(e.target.value) || 0;
                                          setFieldValue(`purchasedItems[${index}].sellingPrice`, val);

                                          // Propagate to serialized items
                                          if (item.isSerialized && item.serializedItems) {
                                            const updatedSerials = item.serializedItems.map(s => ({ ...s, sellingPrice: val }));
                                            setFieldValue(`purchasedItems[${index}].serializedItems`, updatedSerials);
                                          }
                                        }}
                                      />
                                    </div>
                                    <Button variant="outline-primary" size="sm" className="w-100 x-small py-0" onClick={() => {
                                      setB2BItemIndex(index);
                                      setShowB2BModal(true);
                                    }}>
                                      B2B Pricing
                                    </Button>
                                  </td>
                                  <td className="text-end pe-4 fw-bold">
                                    {formatCurrency(item.purchaseQty * item.unitCost)}
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex justify-content-center gap-1">
                                      {(itemDetails?.category === 'Device' || item.category === 'Device') && (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="border-0 bg-transparent p-1"
                                          onClick={() => {
                                            setCurrentItemIndex(index);
                                            setSelectedBaseItem(itemDetails || { _id: item.item_id, itemName: item.itemName, category: 'Device' });
                                            setShowVariantModal(true);
                                          }}
                                          title="Edit Device Details"
                                        >
                                          <FaWrench size={16} />
                                        </Button>
                                      )}
                                      <Button variant="outline-danger" size="sm" className="border-0 text-danger bg-transparent p-1" onClick={() => remove(index)}>
                                        <BiTrash size={16} />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}

                            {/* ADD NEW ROW BUTTON IN TABLE FOOTER AREA */}
                            <tr>
                              <td colSpan="8" className="p-2 bg-light border-0">
                                <Button
                                  variant="link"
                                  className="text-decoration-none fw-bold text-primary d-flex align-items-center gap-2 ps-3"
                                  onClick={() =>
                                    push({
                                      item_id: "",
                                      purchaseQty: 1,
                                      unitCost: 0,
                                      sellingPrice: 0,
                                      variationAttributes: [],
                                      isSerialized: false,
                                      serializedItems: [],
                                      condition: "Brand New"
                                    })
                                  }
                                >
                                  <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px' }}><BiPlus /></div>
                                  Add Another Item
                                </Button>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      )}
                    </FieldArray>
                  </div>
                </div>

                {/* BOTTOM SECTION: SUMMARY & ACTIONS */}
                <Row className="mt-5 g-4">
                  <Col md={7}>
                    <div className="p-4 bg-light rounded-3 h-100 border border-dashed text-center d-flex flex-column align-items-center justify-content-center text-muted">
                      <FaHashtag size={24} className="mb-2 opacity-25" />
                      <span className="small">Additional notes or internal comments can be handled here in future updates.</span>
                    </div>
                  </Col>
                  <Col md={5}>
                    <PurchaseSummary items={availableItems} />

                    <div className="d-flex gap-2 mt-4 pt-3 border-top">
                      <Button variant="light" size="lg" className="flex-grow-1 text-muted fw-medium" onClick={() => { if (typeof onSubmit === "function") onSubmit(); }}>
                        Cancel
                      </Button>
                      <Button variant="outline-danger" size="lg" className="px-3" onClick={() => resetForm()} disabled={isSubmitting} title="Clear Form">
                        <BiTrash />
                      </Button>
                      <Button variant="primary" size="lg" className="flex-grow-1 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? <Spinner as="span" animation="border" size="sm" /> : <><BiSave /> Complete Order</>}
                      </Button>
                    </div>
                  </Col>
                </Row>

              </Card.Body>
            </Card>

            {showVariantModal && (
              <VariantAndUnitsModal
                show={showVariantModal}
                onHide={() => setShowVariantModal(false)}
                onSave={handleModalSave}
                baseItem={selectedBaseItem}
                existingData={values.purchasedItems[currentItemIndex]}
              />
            )}

            {showSupplierDetails && (
              <SupplierDetails
                supplier={getSelectedSupplier(values.supplier)}
                onClose={() => setShowSupplierDetails(false)}
              />
            )}

            {/* Quick KYC Modal */}
            <BrandModal
              show={showKYCModal}
              onHide={() => setShowKYCModal(false)}
              title="Identity Verification (KYC)"
              subtitle="Mandatory for legal compliance and CID investigation safety"
              icon={<FaFingerprint />}
              size="xl"
            >
              <div className="alert alert-warning border-0 shadow-sm mb-4 d-flex gap-3">
                <FaExclamationTriangle className="text-warning mt-1" size={24} />
                <div>
                  <h6 className="fw-bold mb-1">Why is this required?</h6>
                  <p className="small mb-0">To protect your business from stolen property cases, you must record the seller's NIC/Passport number and upload clear photos of their physical ID. These records are vital for Police/CID inquiries.</p>
                </div>
              </div>
              <CustomerForm
                isEditMode={true}
                selectedCustomer={getSelectedCustomer(values.customer)}
                handleClose={() => {
                  setShowKYCModal(false);
                  dispatch(fetchCustomers());
                }}
                savedCompleted={() => {
                  dispatch(fetchCustomers());
                }}
              />
            </BrandModal>
            <B2BPricingModal
              show={showB2BModal}
              onHide={() => {
                setShowB2BModal(false);
                setB2BItemIndex(null);
              }}
              initialPricing={b2bItemIndex !== null ? values.purchasedItems[b2bItemIndex].b2bPricing || [] : []}
              onSave={(pricing) => {
                if (b2bItemIndex !== null) {
                  setFieldValue(`purchasedItems[${b2bItemIndex}].b2bPricing`, pricing);
                }
              }}
            />
          </Form>
        );
      }}
    </Formik >
  );
};

const PurchaseSummary = ({ items }) => {
  const { values, setFieldValue } = useFormikContext();
  // useEffect(() => {
  //     const total = values.purchasedItems.reduce((acc, item) => {
  //         const itemDetails = items.find(i => i._id === item.item_id);
  //         let itemTotal = item.purchaseQty * item.unitCost;
  //         if (itemDetails?.serialized && itemDetails?.category !== 'Device') {
  //             itemTotal = item.serializedItems.reduce((sum, si) => sum + Number(si.unitCost || 0), 0);
  //         } else if (itemDetails?.category === 'Device') {
  //             itemTotal = item.total_price || 0;
  //         }
  //         return acc + itemTotal;
  //     }, 0);

  //     const totalWithDiscount = total - Number(values.purchase_discount || 0);
  //     const totalWithTax = totalWithDiscount + (totalWithDiscount * Number(values.purchase_tax || 0) / 100);
  //     setFieldValue("grand_total", totalWithTax.toFixed(2));
  // }, [values.purchasedItems, values.purchase_discount, values.purchase_tax, setFieldValue, items]);

  useEffect(() => {
    const calculateTotals = () => {
      const subtotal = values.purchasedItems.reduce((acc, item) => {
        const lineTotal = Number(item.total_price || 0);
        const lineDiscount = (lineTotal * Number(item.discount || 0)) / 100;
        return acc + (lineTotal - lineDiscount);
      }, 0);

      const afterGlobalDiscount = subtotal - Number(values.purchase_discount || 0);
      const grandTotal = afterGlobalDiscount + (afterGlobalDiscount * Number(values.purchase_tax || 0) / 100);

      setFieldValue("grand_total", grandTotal.toFixed(2));
      setFieldValue("payment_due_amount", grandTotal.toFixed(2));
      setFieldValue("total_items_count", values.purchasedItems.length);
    };

    calculateTotals();
  }, [setFieldValue, values.purchasedItems, values.purchase_discount, values.purchase_tax]);
  return (
    <Card>
      <Card.Body>
        <h6>Summary</h6>
        <hr />
        <Row>
          <Col>
            <BootstrapForm.Label>Discount (Amt)</BootstrapForm.Label>
            <Field as={FormControl} type="number" name="purchase_discount" />
          </Col>
          <Col>
            <BootstrapForm.Label>Tax (%)</BootstrapForm.Label>
            <Field as={FormControl} type="number" name="purchase_tax" />
          </Col>
        </Row>
        <div className="d-flex justify-content-between align-items-center text-white p-2 mt-3 bg-primary rounded">
          <strong>Grand Total:</strong>
          <strong>{formatCurrency(values.grand_total)}</strong>
        </div>
      </Card.Body>
    </Card>
  );
};
export default CreatePurchaseForm;
